/**
 * Lobby Screen Component
 * Main lobby with table list, filters, and user stats
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../lib/toast';
import { LOBBY_POLL_INTERVAL_MS } from '../../config/app.config';
import { gameService, type LobbyStats } from '../../services/gameService';
import LoadingSpinner from '../shared/LoadingSpinner';
import ErrorState from '../shared/ErrorState';
import TableCard, { Table } from './TableCard';
import CreateTableModal from './CreateTableModal';

const LobbyScreen: React.FC = () => {
  const [tables, setTables] = useState<Table[]>([]);
  const [filteredTables, setFilteredTables] = useState<Table[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('players');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [stats, setStats] = useState<LobbyStats | null>(null);

  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Your stats and the top players.
  useEffect(() => {
    gameService.getLobbyStats().then(setStats).catch(() => setStats(null));
  }, []);

  // Fetch tables
  useEffect(() => {
    fetchTables();
    const interval = setInterval(fetchTables, LOBBY_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // Filter and sort tables
  useEffect(() => {
    let filtered = [...tables];

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter((t) => t.status === filterStatus.toUpperCase());
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter((t) =>
        t.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'players':
          return b.currentPlayers - a.currentPlayers;
        case 'ante':
          return b.anteAmount - a.anteAmount;
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    setFilteredTables(filtered);
  }, [tables, filterStatus, sortBy, searchQuery]);

  const fetchTables = async () => {
    try {
      setError(null);
      const data = await gameService.listTables();
      setTables(data.tables || []);
    } catch (err: any) {
      console.error('Failed to fetch tables:', err);
      setError(err.message || 'Failed to load tables');
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinTable = async (tableId: string) => {
    try {
      // A fixed buy-in (every shared table has one): one tap seats you.
      // Otherwise the table page asks how much to buy in with.
      const table = tables.find((t) => t.id === tableId);
      if (table && table.minBuyIn === table.maxBuyIn) {
        await gameService.joinTable(tableId, { buyInAmount: table.minBuyIn }).catch((error: any) => {
          if (!/already joined/i.test(error?.message ?? '')) throw error;
        });
      }
      navigate(`/game/${tableId}`);
    } catch (error: any) {
      showToast(error.message || 'Failed to join table', 'error');
    }
  };

  const handleSpectate = (tableId: string) => {
    navigate(`/game/${tableId}?spectate=true`);
  };

  const formatChips = (chips: number): string => {
    if (chips >= 1000000) return `${(chips / 1000000).toFixed(1)}M`;
    if (chips >= 1000) return `${(chips / 1000).toFixed(1)}K`;
    return chips.toString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
                P³
              </div>
              <div>
                <h1 className="text-base sm:text-xl font-bold text-white">PokerCubed</h1>
                <p className="text-xs text-gray-400 hidden sm:block">Lobby</p>
              </div>
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Admin shortcut — only visible to admins */}
              {isAdmin && (
                <Link
                  to="/admin"
                  className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-purple-700 hover:bg-purple-600 text-white text-xs font-semibold rounded-lg transition-colors"
                >
                  <span>&#9881;&#65039;</span> Admin
                </Link>
              )}
              <div className="text-right">
                <div className="text-xs sm:text-sm font-semibold text-white">{user?.username}</div>
              </div>
              <button
                onClick={() => navigate('/profile')}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white font-bold text-base sm:text-lg hover:scale-110 transition-transform"
              >
                {user?.username.charAt(0).toUpperCase()}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-3 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 sm:gap-6">
          {/* Sidebar */}
          {/* Phones: the tables come first, the panels after them. */}
          <div className="lg:col-span-1 space-y-3 sm:space-y-4 order-last lg:order-none">
            {/* User Stats Card */}
            <motion.div
              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg sm:rounded-xl border-2 border-gray-700 p-4 sm:p-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h3 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4">Your Stats</h3>
              {/* Every game starts everyone on the same chips, so what counts is how you play them. */}
              <div className="space-y-2 sm:space-y-3">
                {([
                  ['Hands played', String(stats?.you.handsPlayed ?? 0)],
                  [
                    'Hands won',
                    stats && stats.you.handsPlayed > 0
                      ? `${stats.you.handsWon} (${Math.round((100 * stats.you.handsWon) / stats.you.handsPlayed)}%)`
                      : '0',
                  ],
                  ['Games won', String(stats?.you.gamesWon ?? 0)],
                  ['Biggest pot', formatChips(stats?.you.biggestWin ?? 0)],
                ] as const).map(([label, value]) => (
                  <div key={label} className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm text-gray-400">{label}</span>
                    <span className="text-xs sm:text-sm font-semibold text-white tabular-nums">{value}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/profile')}
                className="w-full mt-3 sm:mt-4 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-700 text-white text-sm rounded-lg font-semibold transition-colors"
              >
                View Profile
              </button>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg sm:rounded-xl border-2 border-gray-700 p-4 sm:p-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="w-full px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 active:bg-green-700 text-white text-sm rounded-lg font-semibold transition-colors"
                >
                  &#43; Create Table
                </button>
                <button
                  onClick={() => navigate('/solo')}
                  className="w-full px-3 sm:px-4 py-2 bg-purple-600 hover:bg-purple-700 active:bg-purple-700 text-white text-sm rounded-lg font-semibold transition-colors"
                >
                  &#129302; Play vs Bots
                </button>
                <button
                  onClick={() => navigate('/how-to-play')}
                  className="w-full px-3 sm:px-4 py-2 bg-sky-600 hover:bg-sky-700 active:bg-sky-700 text-white text-sm rounded-lg font-semibold transition-colors"
                >
                  &#128214; How to Play
                </button>
                <button
                  onClick={() => navigate('/specials')}
                  className="w-full px-3 sm:px-4 py-2 bg-amber-600 hover:bg-amber-700 active:bg-amber-700 text-white text-sm rounded-lg font-semibold transition-colors"
                >
                  &#10067; Special Cards
                </button>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="block w-full px-3 sm:px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg font-semibold transition-colors text-center"
                  >
                    &#9881;&#65039; Admin Panel
                  </Link>
                )}
                <button
                  onClick={() => logout()}
                  className="w-full px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 active:bg-red-700 text-white text-sm rounded-lg font-semibold transition-colors"
                >
                  &#128682; Logout
                </button>
              </div>
            </motion.div>

            {/* Leaderboard Preview */}
            <motion.div
              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg sm:rounded-xl border-2 border-gray-700 p-4 sm:p-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4">🏆 Top Players</h3>
              {/* Most games won, then most hands won. */}
              {stats && stats.top.length > 0 ? (
                <div className="space-y-2">
                  {stats.top.slice(0, 3).map((p, i) => (
                    <div key={p.username} className="flex items-center gap-2 sm:gap-3 p-2 bg-gray-900/50 rounded-lg">
                      <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? 'bg-yellow-500' : i === 1 ? 'bg-gray-400' : 'bg-orange-600'
                      } text-white`}>
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-xs sm:text-sm font-semibold text-white">{p.username}</div>
                        <div className="text-xs text-gray-400">
                          {p.gamesWon} {p.gamesWon === 1 ? 'game' : 'games'} won · {p.handsWon} {p.handsWon === 1 ? 'hand' : 'hands'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs sm:text-sm text-gray-500">No games played yet.</p>
              )}
            </motion.div>
          </div>

          {/* Tables Grid */}
          <div className="lg:col-span-3">
            {/* Phones: the two ways to start playing, above the tables. */}
            <div className="lg:hidden grid grid-cols-2 gap-2 mb-3">
              <button
                onClick={() => navigate('/solo')}
                className="px-3 py-2.5 bg-purple-600 active:bg-purple-700 text-white text-sm rounded-lg font-semibold"
              >
                &#129302; Play vs Bots
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-3 py-2.5 bg-green-600 active:bg-green-700 text-white text-sm rounded-lg font-semibold"
              >
                &#43; Create Table
              </button>
            </div>

            {/* Filters — only once there are enough tables to need them. */}
            <div className={`mb-3 sm:mb-6 space-y-3 sm:space-y-4 ${tables.length <= 6 ? 'hidden' : ''}`}>
              {/* Search */}
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="🔍 Search tables..."
                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-800 border-2 border-gray-700 rounded-lg text-sm sm:text-base text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
              />

              {/* Filter and Sort */}
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="flex-1 min-w-[120px] px-3 sm:px-4 py-2 bg-gray-800 border-2 border-gray-700 rounded-lg text-sm sm:text-base text-white focus:outline-none focus:border-blue-500 transition-colors"
                >
                  <option value="all">All Tables</option>
                  <option value="waiting">Waiting</option>
                  <option value="active">Active</option>
                </select>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="flex-1 min-w-[120px] px-3 sm:px-4 py-2 bg-gray-800 border-2 border-gray-700 rounded-lg text-sm sm:text-base text-white focus:outline-none focus:border-blue-500 transition-colors"
                >
                  <option value="players">Sort by Players</option>
                  <option value="ante">Sort by Ante</option>
                  <option value="name">Sort by Name</option>
                </select>

                <div className="w-full sm:w-auto sm:ml-auto text-xs sm:text-sm text-gray-400 flex items-center justify-center sm:justify-start">
                  {filteredTables.length} {filteredTables.length === 1 ? 'table' : 'tables'}
                </div>
              </div>
            </div>

            {/* Tables */}
            {isLoading ? (
              <LoadingSpinner message="Loading tables..." />
            ) : error ? (
              <ErrorState
                dark
                message={error}
                onRetry={fetchTables}
              />
            ) : filteredTables.length === 0 ? (
              <div className="text-center py-12 sm:py-20">
                <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">🎲</div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">No tables found</h3>
                <p className="text-sm sm:text-base text-gray-400 mb-4 sm:mb-6 px-4">
                  {searchQuery || filterStatus !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Be the first to create a table!'}
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 sm:px-6 py-2 sm:py-3 bg-green-600 hover:bg-green-700 active:bg-green-700 text-white text-sm rounded-lg font-semibold transition-colors"
                >
                  Create Table
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {filteredTables.map((table, index) => (
                  <motion.div
                    key={table.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <TableCard
                      table={table}
                      onJoin={handleJoinTable}
                      onSpectate={handleSpectate}
                    />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Create Table Modal */}
      {showCreateModal && (
        <CreateTableModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(tableId) => {
            setShowCreateModal(false);
            fetchTables();
            navigate(`/game/${tableId}`);
          }}
        />
      )}
    </div>
  );
};

export default LobbyScreen;
